import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type {
  ReporteMargen,
  ReporteVentasPeriodo,
  ReporteVentasCliente,
  ReporteVentasVendedor,
  FiltroReporte,
} from '../types/reporte.types';
import { ReporteVentaService } from '../services/reporteVentaService';

interface ReporteState {
  margenes: ReporteMargen[];
  ventasPeriodo: ReporteVentasPeriodo[];
  ventasCliente: ReporteVentasCliente[];
  ventasVendedor: ReporteVentasVendedor[];
  filtro: FiltroReporte;
  anioSeleccionado: number;
  loading: boolean;
  error: string | null;

  fetchMargenes: () => Promise<void>;
  fetchVentasPeriodo: () => Promise<void>;
  fetchVentasCliente: () => Promise<void>;
  fetchVentasVendedor: () => Promise<void>;
  setFiltro: (filtro: Partial<FiltroReporte>) => void;
  setAnio: (anio: number) => void;
  clearError: () => void;
  reset: () => void;
}

const initialState = {
  margenes: [],
  ventasPeriodo: [],
  ventasCliente: [],
  ventasVendedor: [],
  filtro: {} as FiltroReporte,
  anioSeleccionado: new Date().getFullYear(),
  loading: false,
  error: null,
};

export const useReporteStore = create<ReporteState>()(
  devtools(
    (set, get) => ({
      ...initialState,

      fetchMargenes: async () => {
        set({ loading: true, error: null }, false, 'reporte/margenes/start');
        try {
          const margenes = await ReporteVentaService.getMargenes(get().filtro);
          set({ margenes, loading: false }, false, 'reporte/margenes/success');
        } catch (error) {
          set({ loading: false, error: error instanceof Error ? error.message : 'Error al cargar margenes' }, false, 'reporte/margenes/error');
        }
      },

      fetchVentasPeriodo: async () => {
        set({ loading: true, error: null }, false, 'reporte/periodo/start');
        try {
          const ventasPeriodo = await ReporteVentaService.getVentasPeriodo(get().anioSeleccionado);
          set({ ventasPeriodo, loading: false }, false, 'reporte/periodo/success');
        } catch (error) {
          set({ loading: false, error: error instanceof Error ? error.message : 'Error al cargar ventas por periodo' }, false, 'reporte/periodo/error');
        }
      },

      fetchVentasCliente: async () => {
        set({ loading: true, error: null }, false, 'reporte/cliente/start');
        try {
          const ventasCliente = await ReporteVentaService.getVentasCliente(get().filtro);
          set({ ventasCliente, loading: false }, false, 'reporte/cliente/success');
        } catch (error) {
          set({ loading: false, error: error instanceof Error ? error.message : 'Error al cargar ventas por cliente' }, false, 'reporte/cliente/error');
        }
      },

      fetchVentasVendedor: async () => {
        set({ loading: true, error: null }, false, 'reporte/vendedor/start');
        try {
          const ventasVendedor = await ReporteVentaService.getVentasVendedor(get().filtro);
          set({ ventasVendedor, loading: false }, false, 'reporte/vendedor/success');
        } catch (error) {
          set({ loading: false, error: error instanceof Error ? error.message : 'Error al cargar ventas por vendedor' }, false, 'reporte/vendedor/error');
        }
      },

      setFiltro: (filtro) => set((state) => ({ filtro: { ...state.filtro, ...filtro } }), false, 'reporte/setFiltro'),
      setAnio: (anio) => set({ anioSeleccionado: anio }, false, 'reporte/setAnio'),
      clearError: () => set({ error: null }, false, 'reporte/clearError'),
      reset: () => set(initialState, false, 'reporte/reset'),
    }),
    { name: 'reporte-ventas-store' }
  )
);
